import { useState } from "react";
import type { Node } from "@xyflow/react";
import { AGENT_TYPES, AGENT_TYPE_LABELS, BEDROCK_MODELS } from "@ligament/shared";
import { X, Plug, Clock, ArrowRightLeft, Settings2, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ContextPicker } from "./ContextPicker";

interface NodeConfigDrawerProps {
  node: Node;
  onUpdate: (data: Record<string, unknown>) => void;
  onClose: () => void;
}

/* ─── Reusable input primitives ─── */

function TextInput({
  label,
  value,
  onChange,
  placeholder,
  helperText,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  helperText?: string;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 block w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
      />
      {helperText && <p className="mt-1 text-[10px] text-muted-foreground">{helperText}</p>}
    </label>
  );
}

function NumberInput({
  label,
  value,
  onChange,
  placeholder,
  min,
  max,
  helperText,
}: {
  label: string;
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  placeholder?: string;
  min?: number;
  max?: number;
  helperText?: string;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <input
        type="number"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}
        placeholder={placeholder}
        min={min}
        max={max}
        className="mt-1 block w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
      />
      {helperText && <p className="mt-1 text-[10px] text-muted-foreground">{helperText}</p>}
    </label>
  );
}

function SelectInput({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 block w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function TextAreaInput({
  label,
  value,
  onChange,
  placeholder,
  rows = 4,
  mono,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  mono?: boolean;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        className={cn(
          "mt-1 block w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary resize-none",
          mono && "font-mono text-xs",
        )}
      />
    </label>
  );
}

function CheckboxInput({
  label,
  checked,
  onChange,
  description,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  description?: string;
}) {
  return (
    <label className="flex items-start gap-2.5 cursor-pointer group">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 rounded border-border text-primary focus:ring-primary"
      />
      <div>
        <span className="text-xs font-medium text-foreground group-hover:text-primary transition-colors">{label}</span>
        {description && <p className="text-[10px] text-muted-foreground">{description}</p>}
      </div>
    </label>
  );
}

/* ─── Connector definitions ─── */

const AVAILABLE_CONNECTORS = [
  { id: "ehr_nextgen", label: "NextGen EHR (FHIR R4)", category: "EHR", description: "Patient records, appointments, documents, providers, coverage" },
  { id: "looker_analytics", label: "Looker Analytics", category: "BI", description: "Revenue cycle, clinical, scheduling, operational analytics" },
  { id: "availity", label: "Availity", category: "RCM", description: "Eligibility, claims, prior auth, remittance, provider enrollment" },
  { id: "waystar", label: "Waystar", category: "RCM", description: "Claims management, denial management, payment analytics, patient estimation" },
  { id: "payer_eligibility", label: "Eligibility Verification", category: "Payer", description: "Real-time insurance eligibility checks (X12 270/271)" },
  { id: "claim_submission", label: "Claim Submission", category: "Payer", description: "Professional claim submission (X12 837P)" },
  { id: "claim_status", label: "Claim Status", category: "Payer", description: "Claim status inquiry (X12 276/277)" },
  { id: "era_processing", label: "ERA Processing", category: "Payer", description: "Electronic remittance advice (X12 835)" },
  { id: "prior_auth", label: "Prior Auth Automation", category: "Payer", description: "Automated PA submission, status tracking, appeal generation" },
  { id: "sms", label: "SMS (Twilio)", category: "Communication", description: "HIPAA-compliant SMS for appointment reminders, auth notices" },
  { id: "email_ses", label: "Email (SES)", category: "Communication", description: "Transactional email via AWS SES" },
  { id: "fax", label: "Clinical Fax", category: "Communication", description: "eFax/SRFax for referrals, auth letters, clinical documents" },
  { id: "lab_interface", label: "Lab Interface (HL7v2)", category: "Clinical", description: "Lab orders and results via HL7v2 messages" },
  { id: "direct_messaging", label: "Direct Messaging", category: "Clinical", description: "Secure provider-to-provider messaging (Direct Protocol)" },
  { id: "audit_log", label: "Compliance Audit Log", category: "Compliance", description: "Immutable audit trail via CloudWatch" },
] as const;

type ConnectorId = typeof AVAILABLE_CONNECTORS[number]["id"];

/* ─── Agent Config Tabs ─── */

type AgentTab = "general" | "connectors" | "contexts" | "scheduling" | "handoff";

const AGENT_TABS: { id: AgentTab; label: string; icon: React.ReactNode }[] = [
  { id: "general", label: "General", icon: <Settings2 className="h-3 w-3" /> },
  { id: "connectors", label: "Connectors", icon: <Plug className="h-3 w-3" /> },
  { id: "contexts", label: "Contexts", icon: <Layers className="h-3 w-3" /> },
  { id: "scheduling", label: "Scheduling", icon: <Clock className="h-3 w-3" /> },
  { id: "handoff", label: "Handoff", icon: <ArrowRightLeft className="h-3 w-3" /> },
];

/* ─── General Tab (existing fields + enhancements) ─── */

function AgentGeneralTab({ node, onUpdate }: Pick<NodeConfigDrawerProps, "node" | "onUpdate">) {
  const data = node.data as Record<string, unknown>;
  const name = (data.name as string) ?? "";
  const agentType = (data.agentType as string) ?? "general";
  const modelId = (data.modelId as string) ?? "";
  const systemPrompt = (data.systemPrompt as string) ?? "";
  const rulesets = (data.rulesets as string) ?? "";
  const temperature = data.temperature as number | undefined;
  const maxTokens = data.maxTokens as number | undefined;

  return (
    <div className="space-y-4">
      <TextInput label="Agent Name" value={name} onChange={(v) => onUpdate({ name: v })} placeholder="e.g. Intake Agent" />
      <SelectInput
        label="Agent Type"
        value={agentType}
        options={AGENT_TYPES.map((t) => ({ value: t, label: AGENT_TYPE_LABELS[t] }))}
        onChange={(v) => onUpdate({ agentType: v })}
      />
      <SelectInput
        label="Model"
        value={modelId}
        options={BEDROCK_MODELS.map((m) => ({ value: m.id, label: `${m.label} (${m.tier})` }))}
        onChange={(v) => onUpdate({ modelId: v })}
      />
      <NumberInput
        label="Temperature"
        value={temperature}
        onChange={(v) => onUpdate({ temperature: v })}
        placeholder="0.0 - 1.0"
        min={0}
        max={1}
        helperText="Lower = more deterministic. Default: 0.1 for healthcare."
      />
      <NumberInput
        label="Max Tokens"
        value={maxTokens}
        onChange={(v) => onUpdate({ maxTokens: v })}
        placeholder="e.g. 4096"
        min={1}
        max={200000}
        helperText="Maximum output tokens per response."
      />
      <TextAreaInput
        label="System Prompt"
        value={systemPrompt}
        onChange={(v) => onUpdate({ systemPrompt: v })}
        placeholder="You are a healthcare agent specializing in..."
        rows={6}
      />
      <TextAreaInput
        label="Rulesets"
        value={rulesets}
        onChange={(v) => onUpdate({ rulesets: v })}
        placeholder="One rule per line..."
        rows={4}
      />
    </div>
  );
}

/* ─── Connectors Tab ─── */

function AgentConnectorsTab({ node, onUpdate }: Pick<NodeConfigDrawerProps, "node" | "onUpdate">) {
  const data = node.data as Record<string, unknown>;
  const connectors = (data.connectors as ConnectorId[]) ?? [];

  const toggleConnector = (id: ConnectorId) => {
    const updated = connectors.includes(id)
      ? connectors.filter((c) => c !== id)
      : [...connectors, id];
    onUpdate({ connectors: updated });
  };

  // Group by category
  const categories = [...new Set(AVAILABLE_CONNECTORS.map((c) => c.category))];

  return (
    <div className="space-y-4">
      <p className="text-[10px] text-muted-foreground">
        Select which integrations this agent can access. Each connector becomes a tool the agent can call.
      </p>
      {categories.map((category) => (
        <div key={category}>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">{category}</p>
          <div className="space-y-1.5">
            {AVAILABLE_CONNECTORS.filter((c) => c.category === category).map((connector) => (
              <CheckboxInput
                key={connector.id}
                label={connector.label}
                checked={connectors.includes(connector.id)}
                onChange={() => toggleConnector(connector.id)}
                description={connector.description}
              />
            ))}
          </div>
        </div>
      ))}
      {connectors.length > 0 && (
        <div className="pt-2 border-t border-border">
          <p className="text-[10px] text-muted-foreground">
            {connectors.length} connector{connectors.length !== 1 ? "s" : ""} enabled
          </p>
        </div>
      )}
    </div>
  );
}

/* ─── Scheduling Tab ─── */

const WAKEUP_TRIGGER_OPTIONS = [
  { value: "on_demand", label: "On Demand" },
  { value: "timer", label: "Timer (Cron)" },
  { value: "assignment", label: "On Assignment" },
  { value: "automation", label: "Automation" },
  { value: "graph_execution", label: "Agent Execution" },
];

function AgentSchedulingTab({ node, onUpdate }: Pick<NodeConfigDrawerProps, "node" | "onUpdate">) {
  const data = node.data as Record<string, unknown>;
  const wakeupTrigger = (data.wakeupTrigger as string) ?? "on_demand";
  const cronExpression = (data.cronExpression as string) ?? "";
  const concurrencyLimit = data.concurrencyLimit as number | undefined;
  const heartbeatEnabled = (data.heartbeatEnabled as boolean) ?? false;
  const budgetMonthlyCents = data.budgetMonthlyCents as number | undefined;

  return (
    <div className="space-y-4">
      <CheckboxInput
        label="Enable Heartbeat"
        checked={heartbeatEnabled}
        onChange={(v) => onUpdate({ heartbeatEnabled: v })}
        description="When deployed, this agent will run on a schedule via the heartbeat system."
      />
      {heartbeatEnabled && (
        <>
          <SelectInput
            label="Wakeup Trigger"
            value={wakeupTrigger}
            options={WAKEUP_TRIGGER_OPTIONS}
            onChange={(v) => onUpdate({ wakeupTrigger: v })}
          />
          {wakeupTrigger === "timer" && (
            <TextInput
              label="Cron Expression"
              value={cronExpression}
              onChange={(v) => onUpdate({ cronExpression: v })}
              placeholder="0 */6 * * *"
              helperText="Standard 5-field cron. e.g. '0 9 * * 1-5' = weekdays at 9am"
            />
          )}
          <NumberInput
            label="Concurrency Limit"
            value={concurrencyLimit}
            onChange={(v) => onUpdate({ concurrencyLimit: v })}
            placeholder="1"
            min={1}
            max={10}
            helperText="Max simultaneous executions of this agent."
          />
          <NumberInput
            label="Monthly Budget (cents)"
            value={budgetMonthlyCents}
            onChange={(v) => onUpdate({ budgetMonthlyCents: v })}
            placeholder="10000"
            min={0}
            helperText="Cost cap. Agent pauses when budget is exceeded. 10000 = $100."
          />
        </>
      )}
    </div>
  );
}

/* ─── Handoff Tab ─── */

function AgentHandoffTab({ node, onUpdate }: Pick<NodeConfigDrawerProps, "node" | "onUpdate">) {
  const data = node.data as Record<string, unknown>;
  const confidenceThreshold = data.confidenceThreshold as number | undefined;
  const escalationTarget = (data.escalationTarget as string) ?? "";
  const requiredCapabilities = (data.requiredCapabilities as string) ?? "";
  const handoffOnLowConfidence = (data.handoffOnLowConfidence as boolean) ?? true;
  const maxRetries = data.maxRetries as number | undefined;

  return (
    <div className="space-y-4">
      <NumberInput
        label="Confidence Threshold (%)"
        value={confidenceThreshold != null ? Math.round(confidenceThreshold * 100) : undefined}
        onChange={(v) => onUpdate({ confidenceThreshold: v != null ? v / 100 : undefined })}
        placeholder="80"
        min={0}
        max={100}
        helperText="Minimum confidence percentage to proceed without human review."
      />
      <CheckboxInput
        label="HITL on Low Confidence"
        checked={handoffOnLowConfidence}
        onChange={(v) => onUpdate({ handoffOnLowConfidence: v })}
        description="Route to human-in-the-loop interrupt when confidence drops below threshold."
      />
      <TextInput
        label="Escalation Target"
        value={escalationTarget}
        onChange={(v) => onUpdate({ escalationTarget: v })}
        placeholder="e.g. supervisor-agent"
        helperText="Agent to escalate to on failure or low confidence."
      />
      <TextAreaInput
        label="Required Capabilities"
        value={requiredCapabilities}
        onChange={(v) => onUpdate({ requiredCapabilities: v })}
        placeholder="ehr_access&#10;payer_query&#10;clinical_review"
        rows={3}
      />
      <p className="text-[10px] text-muted-foreground">
        One capability per line. Other agents must declare matching capabilities to receive handoffs from this agent.
      </p>
      <NumberInput
        label="Max Retries"
        value={maxRetries}
        onChange={(v) => onUpdate({ maxRetries: v })}
        placeholder="3"
        min={0}
        max={10}
        helperText="Max retry attempts before escalating to supervisor."
      />
    </div>
  );
}

/* ─── Tabbed Agent Config ─── */

function AgentNodeConfig({ node, onUpdate }: Pick<NodeConfigDrawerProps, "node" | "onUpdate">) {
  const [activeTab, setActiveTab] = useState<AgentTab>("general");

  return (
    <div className="space-y-3">
      {/* Tab bar */}
      <div className="flex gap-0.5 rounded-lg bg-muted/50 p-0.5">
        {AGENT_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              "flex-1 flex items-center justify-center gap-1 rounded-md px-2 py-1.5 text-[10px] font-medium transition-colors",
              activeTab === tab.id
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === "general" && <AgentGeneralTab node={node} onUpdate={onUpdate} />}
      {activeTab === "connectors" && <AgentConnectorsTab node={node} onUpdate={onUpdate} />}
      {activeTab === "contexts" && (
        <ContextPicker
          value={(node.data.contexts as string[]) ?? []}
          onChange={(ids) => onUpdate({ contexts: ids })}
        />
      )}
      {activeTab === "scheduling" && <AgentSchedulingTab node={node} onUpdate={onUpdate} />}
      {activeTab === "handoff" && <AgentHandoffTab node={node} onUpdate={onUpdate} />}
    </div>
  );
}

/* ─── Other node type configs (unchanged) ─── */

function ToolNodeConfig({ node, onUpdate }: Pick<NodeConfigDrawerProps, "node" | "onUpdate">) {
  const data = node.data as Record<string, unknown>;
  const name = (data.name as string) ?? "";
  const description = (data.description as string) ?? "";

  return (
    <div className="space-y-4">
      <TextInput label="Tool Name" value={name} onChange={(v) => onUpdate({ name: v })} placeholder="e.g. search_records" />
      <TextAreaInput
        label="Description"
        value={description}
        onChange={(v) => onUpdate({ description: v })}
        placeholder="What does this tool do?"
      />
    </div>
  );
}

function RouterNodeConfig({ node, onUpdate }: Pick<NodeConfigDrawerProps, "node" | "onUpdate">) {
  const data = node.data as Record<string, unknown>;
  const name = (data.name as string) ?? "";

  return (
    <div className="space-y-4">
      <TextInput label="Router Name" value={name} onChange={(v) => onUpdate({ name: v })} placeholder="e.g. Triage Router" />
    </div>
  );
}

function InterruptNodeConfig({ node, onUpdate }: Pick<NodeConfigDrawerProps, "node" | "onUpdate">) {
  const data = node.data as Record<string, unknown>;
  const name = (data.name as string) ?? "";

  return (
    <div className="space-y-4">
      <TextInput label="Review Name" value={name} onChange={(v) => onUpdate({ name: v })} placeholder="e.g. Clinical Review" />
    </div>
  );
}

const TRIGGER_EVENT_OPTIONS = [
  { value: "trigger_clinical", label: "Clinical Event" },
  { value: "trigger_claims", label: "Claims Event" },
  { value: "trigger_scheduling", label: "Scheduling Event" },
  { value: "trigger_prior_auth", label: "Prior Auth Event" },
  { value: "trigger_referral", label: "Referral Event" },
];

function TriggerNodeConfig({ node, onUpdate }: Pick<NodeConfigDrawerProps, "node" | "onUpdate">) {
  const data = node.data as Record<string, unknown>;
  const name = (data.name as string) ?? "";
  const eventType = (data.eventType as string) ?? "trigger_clinical";
  const filterExpr = (data.filterExpression as string) ?? "";

  return (
    <div className="space-y-4">
      <TextInput label="Trigger Name" value={name} onChange={(v) => onUpdate({ name: v })} placeholder="e.g. New Patient Created" />
      <SelectInput
        label="Event Type"
        value={eventType}
        options={TRIGGER_EVENT_OPTIONS}
        onChange={(v) => onUpdate({ eventType: v })}
      />
      <TextAreaInput
        label="Filter Expression"
        value={filterExpr}
        onChange={(v) => onUpdate({ filterExpression: v })}
        placeholder="e.g. event.priority === 'urgent'"
        rows={3}
      />
      <p className="text-[10px] text-muted-foreground">
        Optional JavaScript expression to filter which events activate this trigger.
      </p>
    </div>
  );
}

/* ─── Main Drawer ─── */

const NODE_TYPE_LABELS: Record<string, string> = {
  agent: "Agent Configuration",
  tool: "Tool Configuration",
  router: "Router Configuration",
  interrupt: "Interrupt Configuration",
  trigger: "Trigger Configuration",
  start: "Start Node",
  end: "End Node",
};

export function NodeConfigDrawer({ node, onUpdate, onClose }: NodeConfigDrawerProps) {
  const nodeType = node.type ?? "agent";

  return (
    <div className="w-80 shrink-0 border-l border-border bg-card/50 flex flex-col">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h3 className="text-sm font-semibold">{NODE_TYPE_LABELS[nodeType] ?? "Configuration"}</h3>
        <Button variant="ghost" size="icon-sm" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex-1 overflow-auto p-4">
        {nodeType === "agent" && <AgentNodeConfig node={node} onUpdate={onUpdate} />}
        {nodeType === "tool" && <ToolNodeConfig node={node} onUpdate={onUpdate} />}
        {nodeType === "router" && <RouterNodeConfig node={node} onUpdate={onUpdate} />}
        {nodeType === "interrupt" && <InterruptNodeConfig node={node} onUpdate={onUpdate} />}
        {nodeType === "trigger" && <TriggerNodeConfig node={node} onUpdate={onUpdate} />}
        {(nodeType === "start" || nodeType === "end") && (
          <p className="text-sm text-muted-foreground">
            This is a {nodeType} node. No additional configuration needed.
          </p>
        )}
      </div>
    </div>
  );
}
